import type { LfgRole } from "./board-filters";

/** Сбор основной команды по позициям (KAN-1356): пять линий, слот — участник, приглашение или пусто. */

export const TEAM_LANES = ["Top", "Jungle", "Mid", "Adc", "Support"] as const satisfies readonly LfgRole[];
export type TeamLane = (typeof TEAM_LANES)[number];

export interface TeamMemberLite {
  userId?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  role?: string | null;
}

/**
 * Ответ GET /rosters/{id}/invitations в OpenAPI без типа (any) — узкая рукописная форма.
 * TODO(KAN-1356): заменить сгенерированным типом, когда контроллер получит ProducesResponseType с DTO.
 */
export interface TeamInvitationLite {
  invitedUserId: string;
  invitedUserName: string;
  role: string;
  status: string | number;
}

export type TeamSlot =
  | { lane: TeamLane; state: "filled"; userId: string; name: string; avatarUrl: string | null }
  | { lane: TeamLane; state: "invited"; userId: string; name: string }
  | { lane: TeamLane; state: "free" };

const isLane = (v: string | null | undefined): v is TeamLane => (TEAM_LANES as readonly string[]).includes(v ?? "");

/** Ждущее приглашение: бэкенд отдаёт статус строкой или числом enum (Pending = 0). */
const isPending = (status: string | number) => status === "Pending" || status === 0;

export function buildTeamSlots(
  members: readonly TeamMemberLite[],
  invitations: readonly TeamInvitationLite[],
): TeamSlot[] {
  return TEAM_LANES.map((lane): TeamSlot => {
    const member = members.find((m) => isLane(m.role) && m.role === lane && m.userId);
    if (member) {
      return { lane, state: "filled", userId: member.userId!, name: member.displayName ?? "—", avatarUrl: member.avatarUrl ?? null };
    }
    const invite = invitations.find((i) => isPending(i.status) && i.role === lane);
    if (invite) return { lane, state: "invited", userId: invite.invitedUserId, name: invite.invitedUserName };
    return { lane, state: "free" };
  });
}

/** Заполненность как в макете «N/5»: приглашённые в счёт не идут — они ещё не в составе. */
export function filledCount(slots: readonly TeamSlot[]): number {
  return slots.filter((s) => s.state === "filled").length;
}

/** Кого можно позвать: не в составе и не приглашён на другую позицию. */
export function invitableFriends<T extends { id: string }>(
  friends: readonly T[],
  members: readonly TeamMemberLite[],
  invitations: readonly TeamInvitationLite[],
): T[] {
  const busy = new Set<string>([
    ...members.flatMap((m) => (m.userId ? [m.userId] : [])),
    ...invitations.filter((i) => isPending(i.status)).map((i) => i.invitedUserId),
  ]);
  return friends.filter((f) => !busy.has(f.id));
}

/** Сырой ответ приглашений (any в OpenAPI) → только то, что нужно слотам; мусор отбрасывается. */
export function toInvitations(raw: unknown): TeamInvitationLite[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r): TeamInvitationLite[] => {
    if (typeof r !== "object" || r === null) return [];
    const { invitedUserId, invitedUserName, role, status } = r as Record<string, unknown>;
    if (typeof invitedUserId !== "string" || typeof role !== "string") return [];
    return [
      {
        invitedUserId,
        invitedUserName: typeof invitedUserName === "string" ? invitedUserName : "—",
        role,
        status: typeof status === "string" || typeof status === "number" ? status : "",
      },
    ];
  });
}
